import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, ShieldCheck, Loader2, Landmark } from "lucide-react";
import Seo from "@/components/Seo";
import { useSettings } from "@/hooks/useContent";
import { LOGO_URL, COMPANY, track } from "@/lib/site";
import api from "@/lib/api";

export default function PayPage() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const { data: settings } = useSettings();
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const tracked = useRef(false);

  useEffect(() => {
    let stop = false;
    if (!tracked.current) { track("payment_page_open"); tracked.current = true; }
    const load = async () => {
      try {
        const { data } = await api.get(`/payments/${id}`);
        if (!stop) setPayment(data);
        return data;
      } catch {
        if (!stop) setError("This payment link is not valid or has been removed. Please contact our export team.");
        return null;
      }
    };
    load().then((p) => {
      const cameFromCheckout = search.get("session_id") || search.get("payment_id");
      if (p && p.status === "pending" && cameFromCheckout) {
        setChecking(true);
        let tries = 0;
        const timer = setInterval(async () => {
          tries += 1;
          const fresh = await load();
          if (stop) { clearInterval(timer); return; }
          if (fresh?.status === "paid") {
            clearInterval(timer);
            setChecking(false);
            track("payment_paid");
          } else if (tries > 6) {
            clearInterval(timer);
            setChecking(false);
          }
        }, 2500);
      }
    });
    return () => { stop = true; };
  }, [id]);

  const amount = payment ? new Intl.NumberFormat("en-US", { style: "currency", currency: (payment.currency || "usd").toUpperCase() }).format(payment.amount) : "";

  return (
    <>
      <Seo title="Secure Payment — Orvella Overseas" description="Complete your payment to Orvella Overseas Private Limited." path={`/pay/${id}`} />
      <section className="container-x flex min-h-[70vh] items-center justify-center py-14" data-testid="pay-page">
        <div className="card w-full max-w-lg p-7 sm:p-9">
          <img src={LOGO_URL} alt="Orvella Overseas Private Limited logo" className="mx-auto h-12 w-auto object-contain" />
          <p className="mt-4 text-center text-[11px] font-bold uppercase tracking-[0.2em] text-gold-dark">Payment Request</p>

          {error && <p className="mt-6 rounded-lg bg-red-50 px-4 py-3 text-center text-sm text-red-700" data-testid="pay-error">{error}</p>}

          {!payment && !error && (
            <div className="py-10 text-center"><Loader2 size={26} className="mx-auto animate-spin text-gold-dark" /></div>
          )}

          {payment && (
            <>
              <div className="mt-6 border-t border-line pt-5 text-center">
                <p className="text-sm leading-relaxed text-mist">{payment.description}</p>
                <p className="mt-3 font-serif text-4xl font-semibold text-navy" data-testid="pay-amount">{amount}</p>
                <p className="mt-1 text-xs text-mist">For: {payment.client_name}</p>
              </div>

              {payment.status === "paid" ? (
                <div className="mt-6 rounded-xl border border-green-200 bg-green-50 p-5 text-center" data-testid="pay-success">
                  <CheckCircle2 size={34} className="mx-auto text-green-600" />
                  <h2 className="mt-2 font-serif text-xl font-semibold text-navy">Payment received</h2>
                  <p className="mt-1 text-sm text-mist">
                    Thank you. A confirmation has been recorded{payment.paid_at ? ` on ${payment.paid_at.slice(0, 10)}` : ""}.
                    Our export team will follow up with you shortly.
                  </p>
                </div>
              ) : (
                <div className="mt-6" data-testid="pay-pending">
                  {checking ? (
                    <div className="rounded-xl border border-gold/40 bg-gold/10 p-5 text-center">
                      <Loader2 size={22} className="mx-auto animate-spin text-gold-dark" />
                      <p className="mt-2 text-sm font-semibold text-navy">Confirming your payment…</p>
                      <p className="mt-1 text-xs text-mist">This page updates automatically once the payment is confirmed.</p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 rounded-lg bg-sand px-4 py-3 text-sm text-navy">
                        <Clock size={16} className="shrink-0 text-gold-dark" /> Status: awaiting payment
                      </div>
                      {settings?.payment_instructions ? (
                        <div className="mt-4 rounded-xl border border-line p-5">
                          <h3 className="flex items-center gap-2 text-sm font-bold text-navy">
                            <Landmark size={16} className="text-gold-dark" /> Bank Transfer Details
                          </h3>
                          <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink/85">{settings.payment_instructions}</p>
                          <p className="mt-3 text-xs text-mist">
                            After making the transfer, our team confirms receipt and this page updates to “Paid”.
                          </p>
                        </div>
                      ) : (
                        <p className="mt-4 rounded-lg bg-sand px-4 py-3 text-xs leading-relaxed text-mist">
                          Our export team will share payment instructions with you directly. Once payment is received, this page will show as Paid.
                        </p>
                      )}
                      <p className="mt-4 flex items-start gap-2 text-xs text-mist">
                        <ShieldCheck size={14} className="mt-0.5 shrink-0 text-gold-dark" />
                        {COMPANY} never asks for your card details or passwords by email or WhatsApp. If anyone does, it is not us.
                      </p>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}
